import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileDown, Loader2, Check, Type, PaintBucket, Frame, Images, Clapperboard } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const FONT_LABELS = {
    serif: { fr: "ÉDITORIAL", en: "EDITORIAL" },
    sans: { fr: "MODERNE", en: "MODERN" },
    mono: { fr: "MONO", en: "MONO" },
    classique: { fr: "CLASSIQUE", en: "CLASSIC" },
    display: { fr: "TITRAGE", en: "DISPLAY" },
};
const BG_SWATCH = { noir: "#0A0A0C", anthracite: "#16161A", nuit: "#1A1230", creme: "#F2EDE4", ardoise: "#10161C" };
const BORDER_SWATCH = { violet: "#8B5CF6", or: "#D4AF37", creme: "#F2EDE4", rouge: "#E23B3B", blanc: "#FFFFFF" };

// personnalisation du press kit PDF avant téléchargement : médias + fond + bordures + typo
export default function PresskitCustomizer({ slug, lang }) {
    const [assets, setAssets] = useState(null);
    const [err, setErr] = useState("");
    const [font, setFont] = useState("serif");
    const [bg, setBg] = useState("noir");
    const [border, setBorder] = useState("violet");
    const [selPhotos, setSelPhotos] = useState(null); // null = tout
    const [selClips, setSelClips] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        fetch(`${API}/api/artists/${slug}/presskit-assets`, { credentials: "include" })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then(setAssets)
            .catch(() => setErr("assets"));
    }, [slug]);

    if (err || !assets) return null;

    const photos = assets.photos || [];
    const clips = assets.clips || [];
    const isPhotoOn = (id) => selPhotos === null || selPhotos.includes(id);
    const isClipOn = (id) => selClips === null || selClips.includes(id);
    const togglePhoto = (id) =>
        setSelPhotos((cur) => {
            const base = cur === null ? photos.map((p) => p.id) : cur;
            return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
        });
    const toggleClip = (id) =>
        setSelClips((cur) => {
            const base = cur === null ? clips.map((c) => c.id) : cur;
            return base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
        });

    const download = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/artists/${slug}/presskit.pdf/custom`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ bg, border, font, photos: selPhotos ?? [], clips: selClips ?? [] }),
            });
            if (!r.ok) throw new Error();
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `presskit-${slug}-perso.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch { /* silencieux */ }
        setBusy(false);
    };

    const title = (fr, en) => (lang === "fr" ? fr : en);

    return (
        <div data-testid="pk-customizer" className="mt-5 border border-gold/30 bg-gold/[0.03] p-4">
            <p className="font-mono text-[8px] tracking-[0.3em] text-gold">
                {title("PDF — PERSONNALISER AVANT TÉLÉCHARGEMENT", "PDF — CUSTOMIZE BEFORE DOWNLOAD")}
            </p>

            {/* typographie */}
            <div className="mt-4">
                <p className="mb-2 flex items-center gap-2 font-mono text-[8px] tracking-[0.2em] text-cream/50">
                    <Type size={11} className="text-gold" /> TYPOGRAPHIE
                </p>
                <div className="flex flex-wrap gap-1.5">
                    {(assets.fonts || []).map((f) => (
                        <button key={f} data-testid={`pkc-font-${f}`} onClick={() => setFont(f)}
                            className={`border px-2.5 py-1.5 font-mono text-[8px] tracking-[0.15em] transition-all ${font === f ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-cream/50 hover:border-white/40"}`}>
                            {FONT_LABELS[f]?.[lang] || f.toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>

            {/* fond + bordures */}
            <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                    <p className="mb-2 flex items-center gap-2 font-mono text-[8px] tracking-[0.2em] text-cream/50">
                        <PaintBucket size={11} className="text-gold" /> {title("FOND", "BACKGROUND")}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {(assets.bgs || []).map((b) => (
                            <button key={b} data-testid={`pkc-bg-${b}`} onClick={() => setBg(b)} aria-label={b}
                                className={`h-7 w-7 border transition-all ${bg === b ? "scale-110 border-gold" : "border-white/20 hover:border-white/50"}`}
                                style={{ background: BG_SWATCH[b] }} />
                        ))}
                    </div>
                </div>
                <div>
                    <p className="mb-2 flex items-center gap-2 font-mono text-[8px] tracking-[0.2em] text-cream/50">
                        <Frame size={11} className="text-gold" /> {title("BORDURES", "BORDERS")}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {(assets.borders || []).map((b) => (
                            <button key={b} data-testid={`pkc-border-${b}`} onClick={() => setBorder(b)} aria-label={b}
                                className={`h-7 w-7 border transition-all ${border === b ? "scale-110 border-gold" : "border-white/20 hover:border-white/50"}`}
                                style={{ background: BORDER_SWATCH[b] }} />
                        ))}
                    </div>
                </div>
            </div>

            {/* sélection photos */}
            {photos.length > 0 && (
                <div className="mt-4">
                    <p className="mb-2 flex items-center gap-2 font-mono text-[8px] tracking-[0.2em] text-cream/50">
                        <Images size={11} className="text-gold" /> {title("PHOTOS DANS LE PDF", "PHOTOS IN THE PDF")}
                    </p>
                    <div className="grid max-h-44 grid-cols-4 gap-1.5 overflow-y-auto pr-1 md:grid-cols-6">
                        {photos.map((p) => (
                            <button key={p.id} data-testid={`pkc-photo-${p.id.replace(/[^a-z0-9]/gi, "_")}`} onClick={() => togglePhoto(p.id)}
                                className={`relative aspect-square overflow-hidden border transition-all ${isPhotoOn(p.id) ? "border-gold" : "border-white/10 opacity-35 hover:opacity-60"}`}>
                                <img src={p.url} alt="" className="h-full w-full object-cover" loading="lazy" />
                                {isPhotoOn(p.id) && (
                                    <span className="absolute right-0.5 top-0.5 bg-gold p-0.5 text-ink"><Check size={9} /></span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* sélection clips */}
            {clips.length > 0 && (
                <div className="mt-4">
                    <p className="mb-2 flex items-center gap-2 font-mono text-[8px] tracking-[0.2em] text-cream/50">
                        <Clapperboard size={11} className="text-gold" /> {title("CLIPS DANS LE PDF", "CLIPS IN THE PDF")}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {clips.map((cl) => (
                            <button key={cl.id} data-testid={`pkc-clip-${cl.id.replace(/[^a-z0-9]/gi, "_")}`} onClick={() => toggleClip(cl.id)}
                                className={`flex items-center gap-1.5 border px-2 py-1.5 font-mono text-[8px] tracking-[0.1em] transition-all ${isClipOn(cl.id) ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-cream/40 hover:border-white/40"}`}>
                                {isClipOn(cl.id) && <Check size={9} />}
                                {cl.title}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <motion.button
                data-testid="pkc-download"
                onClick={download}
                disabled={busy}
                whileTap={{ scale: 0.97 }}
                className="mt-5 flex w-full items-center justify-center gap-2 border border-gold bg-gold/10 px-4 py-3 font-mono text-[9px] tracking-[0.25em] text-gold transition-all hover:bg-gold hover:text-ink disabled:opacity-50"
            >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
                {title("TÉLÉCHARGER MON PRESS KIT PDF", "DOWNLOAD MY PRESS KIT PDF")}
            </motion.button>
        </div>
    );
}
